import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import enMessages from '@/messages/en.json'
import esMessages from '@/messages/es.json'
import frMessages from '@/messages/fr.json'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Get current locale from pathname
export function getCurrentLocale(pathname?: string): 'en' | 'es' | 'fr' {
  const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '')

  if (path.startsWith('/es')) return 'es'
  if (path.startsWith('/fr')) return 'fr'
  if (path.startsWith('/en')) return 'en'
  if (path === '/' || path === '') return 'fr' // Default to French for root path
  return 'fr' // Default to French for unprefixed paths
}

// Get preferred locale based on browser language
export function getPreferredLocale(): 'en' | 'es' | 'fr' {
  if (typeof window === 'undefined') return 'fr'

  const browserLang = navigator.language || 'fr'
  const langCode = browserLang.split('-')[0] // Get language code without region

  // Default to French if browser language starts with 'fr'
  if (langCode === 'fr') return 'fr'
  // Switch to Spanish if browser language starts with 'es'
  if (langCode === 'es') return 'es'
  // Otherwise default to French
  return 'fr'
}

// Helper function to flatten nested objects for translation keys
function flattenObject(obj: any, prefix: string = ''): Record<string, string> {
  const flattened: Record<string, string> = {}

  Object.keys(obj).forEach(key => {
    const newKey = prefix ? `${prefix}.${key}` : key

    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      // Recursively flatten nested objects
      Object.assign(flattened, flattenObject(obj[key], newKey))
    } else {
      // Add the flattened key-value pair
      flattened[newKey] = obj[key]
    }
  })

  return flattened
}

// Translation replacement function
export function translateText(text: string, locale: string = 'fr'): string {
  if (!text) return text

  // If text contains translation keys, replace them based on locale
  if (text.includes('{{') && text.includes('}}')) {
    // Create translations object with flattened keys
    const createTranslations = (messages: any) => ({
      ...flattenObject(messages.navigation || {}),
      ...flattenObject(messages.common || {}),
      ...flattenObject(messages.contact || {}),
      ...flattenObject(messages.signup || {}),
      ...flattenObject(messages.demo || {})
    })

    let translations: Record<string, string>

    switch (locale) {
      case 'es':
        translations = {
          ...createTranslations(enMessages),
          ...createTranslations(esMessages) // Override with Spanish
        }
        break
      case 'fr':
        translations = {
          ...createTranslations(enMessages),
          ...createTranslations(frMessages) // Override with French
        }
        break
      default:
        translations = createTranslations(enMessages)
        break
    }

    // Replace all translation keys
    let translatedText = text
    Object.entries(translations).forEach(([key, value]) => {
      // Handle both common.cookieConsent.title and cookieConsent.title formats
      const patterns = [
        `{{${key}}}`, // cookieConsent.title
        `{{common.${key}}}` // common.cookieConsent.title (if key doesn't contain common.)
      ]

      patterns.forEach(pattern => {
        translatedText = translatedText.replace(new RegExp(pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value)
      })
    })

    return translatedText
  }

  return text
}
